import {
  RECORDING_EVENT_TYPES,
  RECORDING_FAILURE_REASONS,
  SCREEN_RECORDING_STATUSES,
  type RecordingEventType,
  type RecordingFailure,
  type RecordingFailureReason,
  type ScreenRecordingStartInput,
  type ScreenRecordingStatus,
} from '@/shared-responder-v2-core';
import { InsightRecordingEventType } from '@/survey-graphql';

import { withTimeout } from './shared-responder-v2-gateway-recording-capture.helpers';

export const UPLOAD_FINALIZE_TIMEOUT_MS = 120_000;

/**
 * Ceiling on one closing `updateInsightRecording` write. A stalled connection
 * never rejects a mutation, and these writes run between the `finalizing`
 * status and the terminal one the block screen's retry needs — unbounded, a
 * hung write leaves the responder behind a wall whose only control does nothing.
 */
export const METADATA_WRITE_TIMEOUT_MS = 10_000;

interface FinalizableUpload {
  readonly finalize: () => Promise<void>;
  readonly waitForUploads: () => Promise<void>;
}

/**
 * `waitForUploads`/`finalize` retry for as long as bytes keep advancing, with
 * no ceiling of their own — a responder staring at the outro's "Saving your
 * recording" screen has no such patience. `false` leaves the upload itself
 * still running in the background; it only means the caller stops waiting.
 */
export function finalizeUploadWithinTimeout(
  upload: FinalizableUpload,
  timeoutInMs: number = UPLOAD_FINALIZE_TIMEOUT_MS
): Promise<boolean> {
  return withTimeout(
    upload
      .waitForUploads()
      .catch(() => undefined)
      .then(() => upload.finalize())
      .catch(() => undefined)
      .then(() => true),
    timeoutInMs,
    false
  );
}

/** Mirrors the legacy recorder's list; GCS is told `video/webm` either way. */
export const SCREEN_MIME_CANDIDATES: readonly string[] = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

export const RECORDING_UPLOAD_MIME_TYPE = 'video/webm';

const PERMISSION_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NotAllowedError',
  'NotReadableError',
  'SecurityError',
]);

const PERMISSION_MESSAGE_FRAGMENTS: readonly string[] = [
  'Permission denied',
  'User denied',
];

const FAILURE_STATUSES: Readonly<
  Record<RecordingFailureReason, ScreenRecordingStatus>
> = {
  [RECORDING_FAILURE_REASONS.PermissionDenied]:
    SCREEN_RECORDING_STATUSES.Denied,
  [RECORDING_FAILURE_REASONS.Unsupported]:
    SCREEN_RECORDING_STATUSES.Unsupported,
  [RECORDING_FAILURE_REASONS.CaptureEnded]: SCREEN_RECORDING_STATUSES.Denied,
  [RECORDING_FAILURE_REASONS.UploadFailed]: SCREEN_RECORDING_STATUSES.Failed,
};

const EVENT_TYPES: Readonly<
  Record<RecordingEventType, InsightRecordingEventType>
> = {
  [RECORDING_EVENT_TYPES.PrimaryQuestion]:
    InsightRecordingEventType.PrimaryQuestion,
  [RECORDING_EVENT_TYPES.AiFollowUp]: InsightRecordingEventType.AiFollowUp,
  [RECORDING_EVENT_TYPES.UserTypedResponse]:
    InsightRecordingEventType.UserTypedResponse,
  [RECORDING_EVENT_TYPES.UserVoiceResponse]:
    InsightRecordingEventType.UserVoiceResponse,
};

function toErrorMessage(error: unknown): string | null {
  return error instanceof Error ? error.message : null;
}

export function pickScreenMimeType(
  isSupported: (mimeType: string) => boolean
): string | null {
  return SCREEN_MIME_CANDIDATES.find(isSupported) ?? null;
}

/**
 * Chromium reports a dismissed picker as `NotAllowedError`; Firefox and Safari
 * variously report a message instead of a name, which is why the message is
 * inspected too — the legacy recorder learned this the hard way and its guard
 * is carried over verbatim in behaviour.
 */
export function toRecordingFailure(error: unknown): RecordingFailure {
  const name = error instanceof Error ? error.name : '';
  const message = toErrorMessage(error);
  const isDenial =
    PERMISSION_ERROR_NAMES.has(name) ||
    PERMISSION_MESSAGE_FRAGMENTS.some(
      (fragment) => message?.includes(fragment) ?? false
    );

  return isDenial
    ? {
        reason: RECORDING_FAILURE_REASONS.PermissionDenied,
        message,
        isRetryable: true,
      }
    : {
        reason: RECORDING_FAILURE_REASONS.Unsupported,
        message,
        isRetryable: false,
      };
}

/**
 * The responder stopping the share mid-test is indistinguishable from a denial
 * as far as the session is concerned: capture is gone and the only way forward
 * is to grant it again.
 */
export function toCaptureEndedFailure(): RecordingFailure {
  return {
    reason: RECORDING_FAILURE_REASONS.CaptureEnded,
    message: null,
    isRetryable: true,
  };
}

export function toRecordingUploadFailure(error: unknown): RecordingFailure {
  return {
    reason: RECORDING_FAILURE_REASONS.UploadFailed,
    message: toErrorMessage(error),
    isRetryable: false,
  };
}

export function toRecordingStatus(
  failure: RecordingFailure
): ScreenRecordingStatus {
  return FAILURE_STATUSES[failure.reason];
}

export function shouldIgnoreCaptureEnded(
  status: ScreenRecordingStatus
): boolean {
  return (
    status === SCREEN_RECORDING_STATUSES.Finalizing ||
    status === SCREEN_RECORDING_STATUSES.Idle
  );
}

export function toInsightRecordingEventType(
  type: RecordingEventType
): InsightRecordingEventType {
  return EVENT_TYPES[type];
}

/**
 * Where the recording clock stands, in seconds, with paused stretches removed —
 * a marker has to point at the frame the responder was looking at, and a
 * paused recorder writes no frames.
 */
export function resolveRecordingClockSeconds(args: {
  readonly startedAtMs: number | null;
  readonly nowMs: number;
  readonly pausedTotalMs: number;
  readonly pausedSinceMs: number | null;
}): number {
  if (args.startedAtMs === null) {
    return 0;
  }

  const pausedNow =
    args.pausedSinceMs === null ? 0 : args.nowMs - args.pausedSinceMs;
  const elapsed =
    args.nowMs - args.startedAtMs - args.pausedTotalMs - pausedNow;

  return Math.max(0, elapsed) / 1000;
}

export function resolveMarkerSeconds(args: {
  readonly stampedSeconds: number | undefined;
  readonly nowSeconds: number;
}): number {
  if (args.stampedSeconds === undefined) {
    return args.nowSeconds;
  }

  return Math.min(Math.max(0, args.stampedSeconds), args.nowSeconds);
}

/**
 * How much of what the recorder has produced is confirmed in the bucket, as a
 * 0–1 fraction. Nothing pending and nothing confirmed is reported as complete
 * rather than as zero, so a finished upload never renders a stalled bar.
 */
export function resolveUploadProgress(args: {
  readonly confirmedBytes: number;
  readonly pendingBytes: number;
}): number {
  const total = args.confirmedBytes + args.pendingBytes;

  if (total <= 0) {
    return 1;
  }

  return Math.min(1, Math.max(0, args.confirmedBytes / total));
}

export function buildRecordingFileName(insightId: string): string {
  return `usability-recording-${insightId}.webm`;
}

export const RECORDING_MIC_SOURCES = {
  Own: 'own',
  Shared: 'shared',
  None: 'none',
} as const;

export type RecordingMicSource =
  (typeof RECORDING_MIC_SOURCES)[keyof typeof RECORDING_MIC_SOURCES];

/**
 * Which microphone the screen recording mixes in.
 *
 * `skipMicrophone` outranks `sharesSessionMicrophone` because the responder's
 * speech is being captured on a phone: there is no local stream to borrow, and
 * opening one would prompt the VM's browser for a device nobody is speaking
 * into.
 */
export function resolveRecordingMicSource(
  input: ScreenRecordingStartInput
): RecordingMicSource {
  if (input.skipMicrophone === true) {
    return RECORDING_MIC_SOURCES.None;
  }

  return input.sharesSessionMicrophone
    ? RECORDING_MIC_SOURCES.Shared
    : RECORDING_MIC_SOURCES.Own;
}

export function cloneAudioStream(
  stream: MediaStream | null
): MediaStream | null {
  if (stream === null) return null;

  return new MediaStream(stream.getAudioTracks().map((track) => track.clone()));
}

interface AudioTrackSource {
  readonly getAudioTracks: () => readonly { readonly readyState: string }[];
}

export function isAudioStreamLive(stream: AudioTrackSource | null): boolean {
  return (
    stream?.getAudioTracks().some((track) => track.readyState === 'live') ??
    false
  );
}

/**
 * One field of `updateInsightRecording`, held back until there is an insight row
 * to write it to.
 *
 * The backend materialises the insight on the responder's FIRST answer, and
 * `initiateUnifiedSession` only reserves the id — so the mutation issued the
 * moment capture started rejected with `SURVEY_INSIGHTS_NOT_FOUND`. Awaiting it
 * inside `start` turned that into an unhandled rejection that also aborted the
 * recorder before it ever reached `recording`, which is why re-sharing never
 * cleared the "screen sharing is required" gate.
 */
export type RecordingMetadataPatch =
  | { readonly type: 'gcsKey'; readonly value: string }
  | { readonly type: 'sessionUri'; readonly value: string };

/**
 * Last write per field wins and order is preserved, so a queue drained after a
 * failed flush sends one mutation per field rather than a growing replay.
 */
export function queueRecordingMetadata(
  queue: readonly RecordingMetadataPatch[],
  patch: RecordingMetadataPatch
): readonly RecordingMetadataPatch[] {
  const existing = queue.findIndex((entry) => entry.type === patch.type);

  if (existing === -1) {
    return [...queue, patch];
  }

  return queue.map((entry, index) => (index === existing ? patch : entry));
}
